import React, { useReducer, useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils.js';
import { formatISO, parseISO, addDays } from 'date-fns';
import swal from 'sweetalert';
import { toast } from 'sonner';
import PageTitle from '../../../layouts/PageTitle';
import NewArticleModal from '../Factoring/Articles/NewArticleModal.js';
import NewCustomerModal from '../Factoring/Customers/NewCustomerModal.js';
import { get, isEqual } from 'lodash';

// Enhanced Components
import QuoteActionsBootstrap from './newquotes/QuoteActionsBootstrap.jsx';
import TemplateSelectorBootstrap from './newquotes/TemplateSelectorQuoteBootstrap.jsx';
import QuoteFormBootstrap from './newquotes/QuoteFormBootstrap.jsx';
import CustomerSelectionCard from './CustomerSelectionCard.js';
import DeliveryOptionsCard from './DeliveryOptionsCard.jsx';
import PaymentMethodsCard from './PaymentMethodsCard.jsx';

import { useInvoiceSettings } from '../../../../context/InvoiceSettingsContext';
import { useQuoteSettings } from '../../../../context/QuoteSettingsContext';
import { useChangeTracking } from './hooks/useChangeTracking';
import { quoteReducer, createInitialState } from './reducers/quoteReducers.jsx';
import { validationSchema } from './utils/quoteValidation.jsx';

const transformApiItemsToInternal = (apiItems = []) => {
  return apiItems.map((item, index) => ({
    id: item.id || `item-${index}`, // API uses 'id' for items
    productcode: item.productcode || '',
    description: item.description || '',
    unitprice: parseFloat(item.unitprice || 0), // Handle null unitprice
    quantity: parseFloat(item.quantity || 1),
    unit: item.unit || 'st',
    tax_rate: item.tax_rate || '25%',
    discount: parseFloat(item.discount || 0),
    type: item.type || 'Product',
    item_id: item.id, // Keep original API ID for updates
    article_id: item.article_id, // Keep article reference
    isNew: false // Mark as existing item
  }));
};

const normalizeDataForComparison = (state) => {
  return {
    quoteData: { // Changed from invoiceData
      customerid: state.customerid,
      customer: state.customer ? {
        id: state.customer.id,
        name: state.customer.name,
        email: state.customer.email
      } : null,
      quoteNumber: state.quoteNumber, // Changed from invoiceNumber
      quoteDate: state.quoteDate?.getTime(), // Changed from invoiceDate
      validUntil: state.validUntil?.getTime(), // Changed from dueDate
      currency: state.currency,
      roundTotal: state.roundTotal,
      notes: state.notes,
      status: state.status,
      selectedTemplate: state.selectedTemplate
    },
    items: state.items.map(item => ({
      id: item.id,
      productcode: item.productcode || '',
      description: item.description || '',
      unitprice: Number(item.unitprice) || 0,
      quantity: Number(item.quantity) || 1,
      unit: item.unit || 'st',
      tax_rate: item.tax_rate || '25%',
      discount: Number(item.discount) || 0,
      type: item.type || 'Product'
    })),
    deliveryOptions: state.deliveryOptions,
    paymentMethods: state.paymentMethods
  };
};

const QuoteForm = () => {
  const { companyId, quoteId } = useParams();
  const history = useHistory();
  const isEditMode = Boolean(quoteId);

  // Refs and contexts
  const quoteRef = useRef(null);
  const { invoiceSettings } = useInvoiceSettings();
  const { quoteSettings } = useQuoteSettings();

  // Initialize state with reducer
  const [state, dispatch] = useReducer(
    quoteReducer,
    { quoteId, isEditMode, invoiceSettings, quoteSettings },
    ({ quoteId, isEditMode, invoiceSettings, quoteSettings }) => createInitialState(quoteId, isEditMode, invoiceSettings, quoteSettings)
  );

  // Additional state
  const [articles, setArticles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewArticleModal, setShowNewArticleModal] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Change tracking - only start after data is loaded
  const trackedData = useMemo(() => {
    if (!isDataLoaded) return null;
    return normalizeDataForComparison(state);
  }, [state, isDataLoaded]);

  const { hasChanges, resetTracking, getChangedFields } = useChangeTracking(trackedData);

  // Validation
  const validationErrors = useMemo(() => {
    const errors = {};
    Object.keys(validationSchema).forEach(field => {
      const error = validationSchema[field](state[field], state);
      if (error) errors[field] = error;
    });
    return errors;
  }, [state]);

  const isFormValid = useMemo(() => {
    const hasValidCustomer = Boolean(state.customerid);
    const hasValidItems = state.items && state.items.length > 0;
    const hasValidDates = state.quoteDate && state.validUntil;
    const hasNoCriticalErrors = Object.keys(validationErrors).length === 0;

    console.log('Form validation check:', {
      hasValidCustomer,
      hasValidItems,
      hasValidDates,
      hasNoCriticalErrors,
      validationErrors,
      customerid: state.customerid,
      customer: state.customer,
      itemsCount: state.items?.length
    });

    return hasValidCustomer && hasValidItems && hasValidDates && hasNoCriticalErrors;
  }, [validationErrors, state.customerid, state.customer, state.items, state.quoteDate, state.validUntil]);

  const hasCriticalErrors = useMemo(() => {
    const criticalFields = ['customerid', 'quoteDate', 'validUntil', 'currency'];
    const hasCriticalValidationErrors = criticalFields.some(field => validationErrors[field]);
    const hasNoCustomer = !state.customerid;
    const hasNoItems = !state.items || state.items.length === 0;
    const hasNoDeliveryMethod = !state.deliveryOptions?.method;

    // For existing invoices (edit mode), don't require payment methods to be set
    // Only require them when actually publishing (which is handled in handlePublish)
    const hasNoPaymentMethods = !isEditMode && !Object.values(state.paymentMethods || {}).some(method => method);

    return hasCriticalValidationErrors || hasNoCustomer || hasNoItems || hasNoDeliveryMethod || hasNoPaymentMethods;
  }, [validationErrors, state.customerid, state.items, state.deliveryOptions?.method, state.paymentMethods, isEditMode]);


  const fetchQuoteDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        getApiUrl(`${companyId}/quotes/${quoteId}`),
        { withCredentials: true }
      );

      if (response.data.success) {
        const quote = response.data.data;

        console.log('Raw API quote response:', quote);
        console.log('Delivery fields in API quote response:', {
          delivery_method: quote.delivery_method,
          delivery_email: quote.delivery_email,
          delivery_phone: quote.delivery_phone,
          delivery_subject: quote.delivery_subject,
          delivery_message: quote.delivery_message,
          payment_methods: quote.payment_methods,
          payment_methods_type: typeof quote.payment_methods,
          payment_methods_parsed: quote.payment_methods ? (typeof quote.payment_methods === 'string' ? 'will_parse' : 'already_parsed') : 'empty',
          selected_template: quote.selected_template,
          notes: quote.notes
        });

        // Parse payment methods from stored data
        let paymentMethods = {};
        if (quote.payment_methods) {
          try {
            let methods = quote.payment_methods;

            // If it's a string, parse it as JSON
            if (typeof methods === 'string') {
              methods = JSON.parse(methods);
            }

            // Convert to object format for the UI
            if (Array.isArray(methods)) {
              // Convert array to object format
              methods.forEach(method => {
                paymentMethods[method] = true;
              });
              console.log('Converted quote payment methods array to object:', paymentMethods);
            } else if (typeof methods === 'object' && methods !== null) {
              paymentMethods = methods;
              console.log('Using quote payment methods object directly:', paymentMethods);
            }
          } catch (e) {
            console.warn('Failed to parse quote payment methods:', e, 'Raw value:', quote.payment_methods);
            paymentMethods = {};
          }
        }

        // Parse delivery options from stored fields
        const deliveryOptions = {
          method: quote.delivery_method || 'download',
          email: quote.delivery_email || '',
          phone: quote.delivery_phone || '',
          message: quote.delivery_message || '',
          subject: quote.delivery_subject || 'Offert'
        };

        console.log('Final parsed quote data for frontend:', {
          deliveryOptions,
          paymentMethods,
          selectedTemplate: quote.selected_template
        });

        // Get default validity days from quote settings
        const defaultValidityDays = quoteSettings?.defaultTerms?.validityDays || 30;

        // First, create the basic transformed quote WITH stored delivery options
        const transformedQuote = {
          quoteId: quote.quote_id,
          customerid: quote.customer_id,
          quoteNumber: quote.quote_number || '',
          quoteDate: quote.quote_date ? parseISO(quote.quote_date) : new Date(),
          validUntil: quote.valid_until ? parseISO(quote.valid_until) : addDays(new Date(), defaultValidityDays),
          currency: quote.currency || 'SEK',
          roundTotal: false,
          status: quote.status || 'Draft',
          notes: quote.notes || '',
          selectedTemplate: quote.selected_template || 'classic',
          items: transformApiItemsToInternal(quote.items || []),
          deliveryOptions: deliveryOptions,
          paymentMethods: paymentMethods
        };

        // Load the basic quote data first
        console.log('Dispatching LOAD_QUOTE with payment methods:', transformedQuote.paymentMethods);
        dispatch({ type: 'LOAD_QUOTE', payload: transformedQuote, quoteSettings });

        // Handle customer separately
        if (quote.customer_id) {
          console.log('Processing customer for quote, customer_id:', quote.customer_id);

          // Try to fetch full customer details first
          try {
            const fullCustomer = await fetchCustomerDetails(quote.customer_id);

            if (fullCustomer) {
              console.log('Using full customer details:', fullCustomer);

              // Set the customer with full details
              dispatch({ type: 'SET_CUSTOMER', payload: fullCustomer });

              // Auto-populate delivery options with customer contact information
              const customerEmail = fullCustomer.email ||
                (fullCustomer.contacts && fullCustomer.contacts.length > 0
                  ? fullCustomer.contacts[0].contact_email
                  : '');

              const customerPhone = fullCustomer.phone ||
                (fullCustomer.contacts && fullCustomer.contacts.length > 0
                  ? fullCustomer.contacts[0].contact_phone
                  : '');

              // Update delivery options after customer is set, but only fill empty fields
              dispatch({
                type: 'UPDATE_DELIVERY_OPTIONS',
                payload: {
                  email: deliveryOptions.email || customerEmail,
                  phone: deliveryOptions.phone || customerPhone,
                  method: deliveryOptions.method,
                  message: deliveryOptions.message,
                  subject: deliveryOptions.subject
                }
              });

            } else {
              throw new Error('Failed to fetch full customer details');
            }

          } catch (error) {
            console.log('Using customer data from quote response (flattened fields), full fetch failed:', error);

            // Fallback: Create customer object from flattened quote data fields
            const customerFromQuote = {
              id: quote.customer_id,
              name: quote.customer_name || '',
              email: quote.customer_email || '',
              phone: quote.customer_phone || '',
              orgnumber: quote.customer_orgnumber || '',
              addresses: quote.customer_street1 ? [{
                address_type: 'billing',
                street1: quote.customer_street1,
                street2: '',
                city: quote.customer_city,
                postal_code: quote.customer_postalcode,
                country: quote.customer_country || 'Sverige'
              }] : [],
              contacts: []
            };

            console.log('Transformed customer from flattened quote data:', customerFromQuote);

            dispatch({ type: 'SET_CUSTOMER', payload: customerFromQuote });

            // Auto-populate delivery options, but only fill empty fields
            dispatch({
              type: 'UPDATE_DELIVERY_OPTIONS',
              payload: {
                email: deliveryOptions.email || customerFromQuote.email || '',
                phone: deliveryOptions.phone || customerFromQuote.phone || '',
                method: deliveryOptions.method,
                message: deliveryOptions.message,
                subject: deliveryOptions.subject
              }
            });
          }
        }

        // Mark data as loaded after all operations complete
        setTimeout(() => {
          console.log('Final quote state after loading:', {
            paymentMethods: state.paymentMethods,
            deliveryOptions: state.deliveryOptions,
            selectedTemplate: state.selectedTemplate
          });
          setIsDataLoaded(true);
          setIsInitialized(true);

          // Reset change tracking after data is fully loaded
          setTimeout(() => {
            console.log('Resetting tracking after quote load');
            resetTracking();
          }, 100);
        }, 200); // Longer delay to ensure all state updates complete
      }
    } catch (error) {
      console.error('Error fetching quote:', error);
      toast.error('Kunde inte hämta offertdetaljer');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/sales/customers/${customerId}`),
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching customer:', error);
      return null;
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/articles`),
        { withCredentials: true }
      );

      setArticles(response.data.data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/sales/customers`),
        { withCredentials: true }
      );
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const saveNewCustomer = async (customerData) => {
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/sales/customers`),
        customerData,
        { withCredentials: true }
      );

      console.log('Customer creation response:', response.data);

      // Refresh customers list first
      await fetchCustomers();

      // Close modal
      setShowNewCustomerModal(false);

      // Show success message
      toast.success('Kunden har skapats');

      // Try to find and auto-select the newly created customer
      // We'll do this after the customers list is refreshed
      setTimeout(async () => {
        try {
          // Get the updated customers list and find the newly created one
          const customersResponse = await axios.get(
            getApiUrl(`${companyId}/sales/customers`),
            { withCredentials: true }
          );
          const updatedCustomers = customersResponse.data || [];

          // Find the customer that matches the created data
          let newCustomer = null;

          if (response.data && response.data.customerId) {
            // API returns customerId in the response
            newCustomer = updatedCustomers.find(c => c.id === response.data.customerId);
            console.log('Searching for customer with ID:', response.data.customerId);
          } else if (response.data && response.data.data && response.data.data.id) {
            // Alternative API structure
            newCustomer = updatedCustomers.find(c => c.id === response.data.data.id);
            console.log('Searching for customer with ID (alt structure):', response.data.data.id);
          } else {
            // Fallback: find by unique identifiers
            console.log('Using fallback matching by customer data');
            newCustomer = updatedCustomers.find(c => {
              if (customerData.type === 'company') {
                return (c.orgnumber && c.orgnumber === customerData.orgnumber) ||
                  (c.name && c.name === customerData.name);
              } else {
                // For individuals, the API might combine first_name + last_name into name field
                const fullName = `${customerData.first_name || ''} ${customerData.last_name || ''}`.trim();
                return (c.name && c.name === fullName) ||
                  (c.first_name === customerData.first_name && c.last_name === customerData.last_name);
              }
            });
          }

          if (newCustomer) {
            console.log('Auto-selecting customer:', newCustomer);
            handleFormDataChange({ customer: newCustomer });
          } else {
            console.log('Could not find newly created customer for auto-selection');
          }
        } catch (error) {
          console.error('Error auto-selecting customer:', error);
        }
      }, 500); // Small delay to ensure state updates

      return response.data;
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error('Ett fel uppstod när kunden skulle skapas');
      throw error;
    }
  };

  // Debug effect to track payment methods changes
  useEffect(() => {
    if (isEditMode && state.paymentMethods) {
      console.log('Quote payment methods changed in state:', state.paymentMethods);
    }
  }, [state.paymentMethods, isEditMode]);

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        fetchArticles(),
        fetchCustomers()
      ]);

      if (isEditMode && quoteId) {
        await fetchQuoteDetails();
      } else {
        // For new invoices, mark as initialized immediately
        setIsDataLoaded(true);
        setIsInitialized(true);
        setTimeout(() => resetTracking(), 100);
      }
    };

    loadInitialData();
  }, [quoteId, isEditMode]);


  const handleFormDataChange = useCallback((updates) => {
    console.log('Form data change:', updates);

    if ('customer' in updates) {
      dispatch({ type: 'SET_CUSTOMER', payload: updates.customer });

      // Auto-populate delivery options with customer contact information
      if (updates.customer) {
        const customerEmail = updates.customer.email ||
          (updates.customer.contacts && updates.customer.contacts.length > 0
            ? updates.customer.contacts[0].contact_email
            : '');

        const customerPhone = updates.customer.phone ||
          (updates.customer.contacts && updates.customer.contacts.length > 0
            ? updates.customer.contacts[0].contact_phone
            : '');

        // Use setTimeout to ensure customer is set before updating delivery options
        setTimeout(() => {
          dispatch({
            type: 'UPDATE_DELIVERY_OPTIONS',
            payload: {
              email: customerEmail,
              phone: customerPhone,
              method: state.deliveryOptions?.method || 'download',
              message: state.deliveryOptions?.message || '',
              subject: state.deliveryOptions?.subject || 'Offert'
            }
          });
        }, 50);

        console.log('Auto-populated quote delivery options:', {
          email: customerEmail,
          phone: customerPhone
        });
      }
    } else if ('items' in updates) {
      dispatch({ type: 'SET_ITEMS', payload: updates.items });
    } else {
      dispatch({ type: 'UPDATE_QUOTE_DATA', payload: updates });
    }
  }, []);


  const handleDeliveryMethodChange = useCallback((method) => {
    dispatch({
      type: 'UPDATE_DELIVERY_OPTIONS',
      payload: { method }
    });
  }, []);

  const handleDeliveryDetailsChange = useCallback((updates) => {
    dispatch({
      type: 'UPDATE_DELIVERY_OPTIONS',
      payload: updates
    });
  }, []);

  const handlePaymentMethodChange = useCallback((method) => {
    dispatch({ type: 'TOGGLE_PAYMENT_METHOD', payload: method });
  }, []);

  const handleSaveDraft = useCallback(async () => {
    if (!state.customerid) {
      toast.error('Vänligen välj en kund');
      return null;
    }

    if (state.items.length === 0) {
      toast.error('Lägg till minst en artikel');
      return null;
    }

    setLoading(true);
    try {
      const dataToSend = {
        customer_id: state.customerid,
        quote_number: state.quoteNumber, // Changed from invoice_number
        quote_date: formatISO(state.quoteDate, { representation: 'date' }), // Changed from invoice_date
        valid_until: formatISO(state.validUntil, { representation: 'date' }), // Changed from due_date
        currency: state.currency,
        round_total: state.roundTotal,
        notes: state.notes,
        status: 'Draft',
        selected_template: state.selectedTemplate,
        items: state.items.map(item => ({
          article_id: item.article_id,
          product_code: item.productcode,
          description: item.description,
          unit_price: item.unitprice,
          quantity: item.quantity,
          unit: item.unit,
          tax_rate: item.tax_rate,
          discount: item.discount || 0,
          type: item.type || 'Product',
          item_id: item.id && !item.isNew ? item.id : undefined
        })),
        delivery_options: state.deliveryOptions,
        payment_methods: Object.keys(state.paymentMethods).filter(m => state.paymentMethods[m])
      };

      let response;

      if (isEditMode) {
        response = await axios.put(
          getApiUrl(`${companyId}/quotes/${quoteId}`), // Use quotes endpoint
          dataToSend,
          { withCredentials: true }
        );
      } else {
        response = await axios.post(
          getApiUrl(`${companyId}/quotes`), // Use quotes endpoint
          dataToSend,
          { withCredentials: true }
        );
      }

      if (response.data.success) {
        resetTracking();
        toast.success(isEditMode ? 'Offerten uppdaterad' : 'Offerten sparad');

        // New response structure: response.data.data contains the quote data
        // For updates (edit mode), use existing quoteId if response doesn't include it
        const savedQuoteId = response.data.data?.id || (isEditMode ? quoteId : null);

        // If creating new quote, redirect to edit mode
        if (!isEditMode && savedQuoteId) {
          history.replace(`/${companyId}/edit-quote/${savedQuoteId}`);
        }

        console.log('Saved quote ID:', savedQuoteId);
        return savedQuoteId;
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.message || 'Kunde inte spara offerten');
      return null;
    } finally {
      setLoading(false);
    }
  }, [state, isEditMode, companyId, quoteId, resetTracking, history]);


  const handlePublish = useCallback(async () => {
    if (!state.deliveryOptions.method) {
      toast.warning('Vänligen välj ett leveransalternativ');
      return;
    }

    const hasSelectedPaymentMethod = Object.values(state.paymentMethods).some(v => v);
    if (!hasSelectedPaymentMethod) {
      toast.warning('Vänligen välj minst en betalningsmetod');
      return;
    }

    if (!isFormValid) {
      toast.warning('Vänligen åtgärda valideringsfel');
      return;
    }

    let quoteIdToPublish = quoteId;

    // Save changes before publishing if needed
    if (hasChanges || !isEditMode) {
      const confirmSave = await swal({
        title: hasChanges ? 'Osparade ändringar' : 'Spara och publicera',
        text: hasChanges
          ? 'Vill du spara utkastet innan du publicerar offerten?'
          : 'Offerten måste sparas innan den kan publiceras. Fortsätt?',
        icon: 'warning',
        buttons: ['Avbryt', 'Spara och publicera'],
      });

      if (!confirmSave) {
        return; // User cancelled
      }

      // Save the draft first
      const savedQuoteId = await handleSaveDraft();

      console.log('Saved quote ID:', savedQuoteId);

      if (!savedQuoteId) {
        return; // Save failed, don't proceed with publish
      }

      // Update the ID to use for publishing
      quoteIdToPublish = savedQuoteId;
    }

    // Now proceed with publishing
    try {
      setLoading(true);

      console.log('Publishing quote with ID:', quoteIdToPublish, state.deliveryOptions);

      const response = await axios.post(
        getApiUrl(`${companyId}/quotes/${quoteIdToPublish}/publish`),
        {
          action: state.deliveryOptions.method,
          email: state.deliveryOptions.email,
          phone: state.deliveryOptions.phone,
          subject: state.deliveryOptions.subject,
          message: state.deliveryOptions.message,
          paymentMethods: Object.keys(state.paymentMethods).filter(m => state.paymentMethods[m]),
        },
        {
          withCredentials: true,
          responseType: state.deliveryOptions.method === 'download' ? 'blob' : 'json'
        }
      );

      if (state.deliveryOptions.method === 'download' && response.data) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `offert-${quoteIdToPublish}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      }

      dispatch({ type: 'UPDATE_QUOTE_DATA', payload: { status: 'Sent' } });
      resetTracking();
      toast.success('Offerten publicerad');
      history.push(`/${companyId}/quotes`);
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Kunde inte publicera offerten');
    } finally {
      setLoading(false);
    }
  }, [state, hasChanges, isFormValid, isEditMode, quoteId, companyId, handleSaveDraft, resetTracking, history]);


  const handleDeleteDraft = useCallback(async () => {
    if (!isEditMode || state.status !== 'Draft') {
      return;
    }

    const confirmed = await swal({
      title: "Är du säker?",
      text: "Vill du radera detta offertutkast? Detta kan inte ångras.",
      icon: "warning",
      buttons: ["Avbryt", "Radera utkast"],
      dangerMode: true,
    });

    if (!confirmed) {
      return; // User cancelled
    }

    try {
      setLoading(true);
      const response = await axios.delete(
        getApiUrl(`${companyId}/quotes/${quoteId}`),
        { withCredentials: true }
      );

      if (response.data.success) {
        //swal("Raderad!", "Offertutkastet har raderats.", "success");
        resetTracking(); // Reset tracking to prevent navigation warnings
        history.push(`/${companyId}/quotes`);
      } else {
        throw new Error(response.data.message || 'Något gick fel');
      }
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast.error("Kunde inte radera offertutkastet. Försök igen senare.");
    } finally {
      setLoading(false);
    }
  }, [companyId, quoteId, isEditMode, state.status, history, resetTracking]);


  // Article management
  const handleSaveArticle = async (newArticle) => {
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/articles`),
        newArticle,
        { withCredentials: true }
      );
      if (response.data.success) {
        await fetchArticles();
        toast.success('Artikeln sparades');
      }
    } catch (error) {
      toast.error('Kunde inte spara artikeln');
    }
    setShowNewArticleModal(false);
  };

  useEffect(() => {
    const unblock = history.block((location) => {
      if (hasChanges && isInitialized) {
        const isNavigatingToEditSameQuote = location.pathname.includes(`/edit-quote/`) &&
          !isEditMode;

        if (isNavigatingToEditSameQuote) {
          return true;
        }

        return new Promise((resolve) => {
          swal({
            title: 'Du har osparade ändringar!',
            text: 'Vill du spara utkastet innan du lämnar sidan?',
            icon: 'warning',
            buttons: {
              cancel: 'Avbryt',
              discard: {
                text: 'Fortsätt utan att spara',
                value: 'discard',
              },
              save: {
                text: 'Spara utkast',
                value: 'save',
              },
            },
          }).then((value) => {
            switch (value) {
              case 'save':
                handleSaveDraft().then((savedId) => {
                  resolve(savedId ? true : false);
                });
                break;
              case 'discard':
                resolve(true);
                break;
              default:
                resolve(false);
                break;
            }
          });
        });
      }
      return true;
    });

    return () => unblock();
  }, [hasChanges, isInitialized, history, handleSaveDraft, isEditMode]);

  return (
    <>
      <div className="d-flex flex-column h-100">
        <PageTitle
          activeMenu={isEditMode ? "Redigera Offert" : "Skapa Offert"}
          motherMenu="Offerter"
        />

        {/* Header Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                {isEditMode ? 'Redigera Offert' : 'Skapa Ny Offert'}
                {state.status && (
                  <span className={`badge ms-3 ${state.status === 'Draft' ? 'bg-warning' : 'bg-success'}`}>
                    {state.status === 'Draft' ? 'Utkast' : 'Skickad'}
                  </span>
                )}
                {isEditMode && state.quoteNumber && (
                  <span className="badge bg-info ms-2">#{state.quoteNumber}</span>
                )}
              </h4>
              <p className="text-muted mb-0">
                {isEditMode
                  ? 'Uppdatera offertuppgifter och innehåll'
                  : 'Fyll i kundinformation, lägg till produkter och välj leveransalternativ'
                }
                {hasChanges && isInitialized && <span className="text-warning ms-2">(Osparade ändringar)</span>}
              </p>
            </div>

            <div className="d-flex gap-2">

              <Link
                to={`/${companyId}/quotes`}
                className="btn btn-outline-secondary"
              >
                <i className="bi bi-arrow-left me-2"></i>
                Tillbaka
              </Link>
            </div>
          </div>
        </div>

        {/* Actions Section */}
        <QuoteActionsBootstrap
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublish}
          onDelete={handleDeleteDraft}
          isValid={isFormValid}
          hasUnsavedChanges={hasChanges && isInitialized}
          hasCriticalErrors={hasCriticalErrors}
          loading={loading}
          showDeleteButton={isEditMode && state.status === 'Draft'}
        />

        {/* Customer Selection Section - Now standalone */}
        <CustomerSelectionCard
          customers={customers}
          selectedCustomer={state.customer}
          onCustomerSelect={(customer) => handleFormDataChange({ customer })}
          onFetchCustomerDetails={fetchCustomerDetails}
          onCreateCustomer={() => setShowNewCustomerModal(true)}
          loading={loading}
        />

        {/* Template Selection */}
        {state.customer && (
          <div className="mb-4">
            <TemplateSelectorBootstrap
              selectedTemplate={state.selectedTemplate}
              onSelectTemplate={(template) => dispatch({ type: 'SET_TEMPLATE', payload: template })}
              availableTemplates={invoiceSettings?.templates}
            />
          </div>
        )}

        {/* Delivery Options */}
        {state.customer && (
          <DeliveryOptionsCard
            deliveryMethod={state.deliveryOptions.method}
            onDeliveryMethodChange={handleDeliveryMethodChange}
            deliveryDetails={state.deliveryOptions}
            onDeliveryDetailsChange={handleDeliveryDetailsChange}
            invoiceSettings={invoiceSettings}
          />
        )}

        {/* Payment Methods */}
        {state.customer && (
          <PaymentMethodsCard
            paymentMethods={state.paymentMethods}
            onPaymentMethodChange={handlePaymentMethodChange}
            invoiceSettings={invoiceSettings}
          />
        )}

        {/* Main Form Content */}
        {state.customer && (
          <div className="flex-grow-1 h-100">
            <QuoteFormBootstrap
              formData={{
                customerid: state.customerid,
                customer: state.customer,
                quoteNumber: state.quoteNumber,
                quoteDate: state.quoteDate,
                validUntil: state.validUntil,
                currency: state.currency,
                roundTotal: state.roundTotal,
                items: state.items,
                notes: state.notes,
                defaultVatRate: state.defaultVatRate
              }}
              onFormDataChange={handleFormDataChange}
              availableArticles={articles}
              loading={loading}
              invoiceSettings={invoiceSettings}
              validationErrors={validationErrors}
            />
          </div>
        )}

        {/* New Article Modal */}
        <NewArticleModal
          show={showNewArticleModal}
          handleClose={() => setShowNewArticleModal(false)}
          handleSave={handleSaveArticle}
          articleDetails={selectedArticle}
        />

        {/* New Customer Modal */}
        <NewCustomerModal
          show={showNewCustomerModal}
          handleClose={() => setShowNewCustomerModal(false)}
          handleSave={saveNewCustomer}
        />
      </div>
    </>
  );
};

export default QuoteForm;
